import {ChangeDetectorRef, Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {IBasePageComponent} from '../../../../../sdk/src/components/ibase-page/ibase-page.component';
import {CpmCServiceRequestApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request.api-model';
import {CpmCServiceRequestGroupApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request-group.api-model';
import {CpmCServiceRequestGroupApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request-group.api-service';
import {firstValueFrom, Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {UserCaching} from '../../../caching/user.caching';
import {EDialogOpenMode} from '../../../../../sdk/src/enums/dialog-open-mode.enum';
import {NewEditProjectModalComponent} from '../../../modals/new-edit-project-modal/new-edit-project-modal.component';
import {NewEditOrganizationModalComponent} from '../../../modals/new-edit-organization-modal/new-edit-organization-modal.component';
import {NewEditLocationModalComponent} from '../../../modals/new-edit-location-modal/new-edit-location-modal.component';
import {
    NewEditServiceRequestModalComponent,
    NewEditServiceRequestModalResult
} from '../../../modals/new-edit-service-request-modal/new-edit-service-request-modal.component';
import {IBaseCRUModalResultBase} from '../../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {CpmCUserApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {CpmCTaskApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-task.api-service';
import {CpmCObjectStoreEntryProxyApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {CpmCServiceRequestApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request.api-service';
import {SdkBridge} from '../../../sdk-bridge';
import {NewEditServiceRequestGroupModalComponent} from '../../../modals/new-edit-service-request-group-modal/new-edit-service-request-group-modal.component';
import {ECpmCObjectStoreEntryAttributesProxyApiEnum} from '../../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-object-store-entry-proxy.api-enums';

/**
 * Service Request General Information Component
 *
 * Displays general information for a service request including:
 * - Read-only metadata (ID, organization, project, location, created date/user)
 * - Statistics overview (tasks, files, object store counts)
 *
 * Provides navigation to related tabs and modal dialogs for viewing and editing service request details.
 */
@Component({
    selector: 'app-service-request-general-info',
    templateUrl: './service-request-general-info.component.html',
    styles: `
        .info-col {
            display: flex;
            flex-direction: column;
            align-content: space-between;
            flex-wrap: wrap;
            justify-content: space-around;

            & > div {
                height: 2.5rem;
            }
        }
    `,
    imports: [CommonModule, PrimeNgImportsModule]
})
export class ServiceRequestGeneralInfoComponent extends IBasePageComponent implements OnChanges {
    private readonly cdr = inject(ChangeDetectorRef);

    protected serviceRequestGroups: CpmCServiceRequestGroupApiModel[] = [];
    protected createdByUser?: CpmCUserApiModel;
    protected canEditServiceRequest = false;

    protected tasksCount?: number;
    protected filesCount?: number;
    protected objectStoreCount?: number;

    // ===============================================================
    // ===============================================================

    /**
     * The service request to display and edit
     */
    @Input() serviceRequest?: CpmCServiceRequestApiModel;
    /**
     * Observable that triggers statistics refresh when data changes in other tabs
     */
    @Input() refreshTrigger$?: Observable<void>;

    // ===============================================================
    // ===============================================================

    /**
     * Initializes the component
     */
    constructor() {
        super();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        // Subscribe to refresh trigger to reload statistics when data changes in other tabs
        this.refreshTrigger$?.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.loadCounts();
        });

        this.canEditServiceRequest = SdkBridge.isAuthorizedForRouteResourceUpdate(
            CpmCServiceRequestApiService.endpointEdit(undefined, this.serviceRequest?.uuid ?? '')
        );

        await this.asyncCritical(async () => {
            this.loadingStr = 'Loading service request groups...';
            this.serviceRequestGroups = (
                await firstValueFrom(
                    CpmCServiceRequestGroupApiService.getServiceRequestGroupList(
                        {},
                        CpmCServiceRequestGroupApiService.dPaginatorConfig
                    )
                )
            ).data;

            if (this.serviceRequest?.created_by_uuid) {
                UserCaching.getUserBatched(this.serviceRequest.created_by_uuid).subscribe((user) => {
                    this.createdByUser = user;
                });
            }
        });
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        // No cleanup required
    }

    // ===============================================================
    // ===============================================================

    /**
     * Angular lifecycle hook called when input properties change.
     * Loads related data when serviceRequest changes.
     *
     * @param changes - Object containing the changed properties
     */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['serviceRequest'] && this.serviceRequest) {
            this.loadCounts();
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Loads count statistics for tasks, files, and object store entries.
     * Updates the component properties with the retrieved counts.
     */
    private loadCounts(): void {
        if (!this.serviceRequest?.uuid) return;

        // Tasks count
        firstValueFrom(
            CpmCTaskApiService.getTaskList({service_request_uuid: this.serviceRequest.uuid}, {limit: 1})
        ).then((result) => (this.tasksCount = result.total));

        // TODO: Get files count
        // firstValueFrom(
        //     CpmCFileApiService.getUserFileList({
        //         parent_path: `/service-requests/${this.serviceRequest.uuid}/`
        //     })
        // ).then((result) => (this.filesCount = result.total));

        // Object Store count
        firstValueFrom(
            CpmCObjectStoreEntryProxyApiService.getObjectStoreEntryProxyList(
                'service_request',
                this.serviceRequest.uuid,
                {
                    'attributes(bitnot)': ECpmCObjectStoreEntryAttributesProxyApiEnum.INTERNAL
                },
                {limit: 1}
            )
        ).then((result) => (this.objectStoreCount = result.total));
    }

    // ===============================================================
    // ===============================================================

    /**
     * Opens the service request modal in edit mode
     */
    protected openServiceRequestEditModal(): void {
        if (!this.serviceRequest) return;

        const ref = this.globalsService.dialogService.open(
            NewEditServiceRequestModalComponent,
            NewEditServiceRequestModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.EDIT,
                extended: {
                    existing: this.serviceRequest
                }
            })
        );

        // Refresh service request data when modal closes with changes
        ref.onClose.subscribe(async (result: IBaseCRUModalResultBase<NewEditServiceRequestModalResult>) => {
            const updatedServiceRequest = result?.extended?.item;

            if (updatedServiceRequest && this.serviceRequest) {
                // Update local service request data
                this.serviceRequest.organization_uuid = updatedServiceRequest.organization_uuid;
                this.serviceRequest.organization = updatedServiceRequest.organization;
                this.serviceRequest.location_uuid = updatedServiceRequest.location_uuid;
                this.serviceRequest.location = updatedServiceRequest.location;
                this.serviceRequest.project_uuid = updatedServiceRequest.project_uuid;
                this.serviceRequest.project = updatedServiceRequest.project;
                this.serviceRequest.service_request_group_uuid = updatedServiceRequest.service_request_group_uuid;
                this.serviceRequest.service_request_group = updatedServiceRequest.service_request_group;
                this.serviceRequest.description = updatedServiceRequest.description;

                await this.globalsService.sessionService.changeOrganization(updatedServiceRequest.organization_uuid);
                await this.globalsService.sessionService.changeProject(updatedServiceRequest.project_uuid);

                this.cdr.detectChanges();
            }
        });
    }
    /**
     * Opens the organization modal in read-only mode
     */
    protected openOrganizationModal(): void {
        if (!this.serviceRequest?.organization) return;
        this.globalsService.dialogService.open(
            NewEditOrganizationModalComponent,
            NewEditOrganizationModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.READ_ONLY,
                extended: {
                    existing: this.serviceRequest.organization
                },
                showGoTo: true
            })
        );
    }
    /**
     * Opens the project modal in read-only mode
     */
    protected openProjectModal(): void {
        if (!this.serviceRequest?.project) return;
        this.globalsService.dialogService.open(
            NewEditProjectModalComponent,
            NewEditProjectModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.READ_ONLY,
                extended: {
                    existing: this.serviceRequest.project
                },
                showGoTo: true
            })
        );
    }
    /**
     * Opens the group modal in read-only mode
     */
    protected openGroupModal(): void {
        if (!this.serviceRequest?.service_request_group) return;
        this.globalsService.dialogService.open(
            NewEditServiceRequestGroupModalComponent,
            NewEditServiceRequestGroupModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.READ_ONLY,
                extended: {
                    existing: this.serviceRequest.service_request_group
                },
                showGoTo: false
            })
        );
    }
    /**
     * Opens the location modal in read-only mode
     */
    protected openLocationModal(): void {
        if (!this.serviceRequest?.location) return;
        this.globalsService.dialogService.open(
            NewEditLocationModalComponent,
            NewEditLocationModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.READ_ONLY,
                extended: {
                    existing: this.serviceRequest.location
                },
                showGoTo: true
            })
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Navigates to the specified tab in the parent page.
     * Updates URL query parameters to switch tabs.
     *
     * @param tab - The tab identifier to navigate to
     */
    protected navigateToTab(tab: string): void {
        this.globalsService.router.navigate([], {
            relativeTo: this.globalsService.activatedRoute,
            queryParams: {tab},
            queryParamsHandling: 'merge'
        });
    }
}
